import { useEffect, useState } from 'react';

/**
 * Three routes and one parameter. A routing library on a yearly major-release
 * cadence is a real maintenance cost for that, and on a site whose job is moving
 * photographs, a kilobyte of framework is a kilobyte not spent on an image.
 */

export function usePath(): string {
  const [path, setPath] = useState(() => window.location.pathname);

  useEffect(() => {
    const onPopState = (): void => setPath(window.location.pathname);

    window.addEventListener('popstate', onPopState);
    // navigate() dispatches this, so a programmatic push updates every subscriber
    // -- which popstate alone does not do.
    window.addEventListener('cq:navigate', onPopState);

    return () => {
      window.removeEventListener('popstate', onPopState);
      window.removeEventListener('cq:navigate', onPopState);
    };
  }, []);

  return path;
}

export function navigate(to: string, options: { replace?: boolean } = {}): void {
  if (to === window.location.pathname) return;

  if (options.replace) {
    window.history.replaceState(null, '', to);
  } else {
    window.history.pushState(null, '', to);
  }

  window.dispatchEvent(new Event('cq:navigate'));
}

/** Extracts a single trailing segment, e.g. `/g/as_123` -> `as_123`. */
export function segmentAfter(path: string, prefix: string): string | null {
  if (!path.startsWith(prefix)) return null;

  const rest = path.slice(prefix.length).replace(/^\/+|\/+$/g, '');
  if (rest.length === 0 || rest.includes('/')) return null;

  return rest;
}
